// src/pages/SuccessStoriesPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  FaHome, 
  FaChevronRight, 
  FaFilter, 
  FaTimes, 
  FaSearch,
  FaUser,
  FaBuilding,
  FaGraduationCap,
  FaChartLine,
  FaQuoteLeft,
  FaStar,
  FaCheck,
  FaClock,
} from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import Card from '../components/common/Card';

const SuccessStoriesPage = () => {
  // State for filtering and search
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStories, setFilteredStories] = useState([]);
  const [expandedStory, setExpandedStory] = useState(null);
  
  // Mock success stories data (in a real app, this would come from an API)
  const successStoriesData = [
    {
      id: 1,
      name: "Ing. Aminata Ngaujah",
      role: "Land Surveyor",
      company: "Ministry of Lands, Housing and Country Planning",
      image: "/images/testimonials/aminata_ngaujah.jpg",
      category: "government",
      year: "2022",
      program: "AutoCAD & Global Mapper",
      quote: "While studying Building and Civil Engineering at Milton Margai Technical University and working as a junior surveyor at the Ministry of Lands, I always felt exhausted with the traditional approach that uses drawing boards to do engineering drawings and survey plans. So when I heard about Leotech digital engineering design program using AutoCAD, Global Mapper and other GIS tools, I was happy because I have been looking for such opportunity. So, I joined cohort 8 as a trainee and completed the training in AutoCAD for 2D plotting and 3D design and Global Mapper laying the foundation for my drone piloting skills. These digital tools have given me advantage in my office as the youngest engineer that can use these digital platforms for quick and accurate engineering design.",
      story: "Aminata was working as a junior surveyor at the Ministry of Lands, Housing and Country Planning while studying Building and Civil Engineering at Milton Margai Technical University. She found the traditional approach using drawing boards for engineering drawings and survey plans to be exhausting and inefficient. After joining Leotech's Cohort 8, she mastered AutoCAD for 2D plotting and 3D design, as well as Global Mapper, which laid the foundation for her drone piloting skills. These digital competencies have given her a significant advantage in her office, making her the youngest engineer proficient in these digital platforms for quick and accurate engineering design. Her newfound skills have accelerated her career path and increased her value to her department.",
      impact: "Aminata's training at Leotech transformed her approach to land surveying, enabling her to complete tasks more efficiently and accurately. She has since been recognized for her technical expertise and has taken on greater responsibilities in her role. Her success story illustrates how digital skills training can enhance existing careers, particularly in government institutions that may be slower to adopt new technologies."
    },
    {
      id: 2,
      name: "Ing. Lansana Farroh",
      role: "Civil Engineer",
      company: "Freelance Consultant",
      image: "/images/testimonials/lansana_farroh.jpg",
      category: "freelance",
      year: "2023",
      program: "Chief Architect",
      quote: "Learning Chief Architect from Leotech has significantly enhanced my skills. This powerful design software allows me to create 3D models and detailed floor plans, facilitating better collaboration with architects and contractors. Completing my certification in Chief Architect Software has boosted my confidence and enhanced my professional value. Overall, mastering this software has improved my effectiveness as a civil engineer, allowing me to deliver high-quality solutions and manage freelance work efficiently while balancing my financial responsibilities as I am now a freelance designer.",
      story: "Lansana Farroh, a civil engineer from Fourah Bay College, was looking to expand his professional capabilities when he joined Leotech. He focused on learning Chief Architect software, which has become an invaluable tool in his career. The training enabled him to create sophisticated 3D models and detailed floor plans, significantly improving his ability to collaborate with architects and contractors. After completing his certification, Lansana leveraged his new skills to establish himself as a freelance designer. The technical proficiency gained through Leotech's program has allowed him to deliver higher quality solutions to his clients and manage multiple projects efficiently. His success as a freelancer demonstrates how targeted digital skills training can open new career paths and entrepreneurial opportunities.",
      impact: "Lansana's training at Leotech enabled him to transition from traditional employment to successful freelance work. His expertise in Chief Architect software has made him a sought-after consultant for architectural and construction projects. He now earns a better income while enjoying the flexibility of freelance work, and has built a substantial client base through referrals based on the quality of his design work."
    },
    {
      id: 3,
      name: "Ing. Agnes Wurrie",
      role: "Project Engineer",
      company: "SOGEFEL (SL) Limited",
      image: "/images/testimonials/agnes_wurrie.jpg",
      category: "private-sector",
      year: "2021",
      program: "AutoCAD",
      quote: "I came to know about Leotech through a friend who updated his status using their flyer. So I contacted him and said, oh I am interested in this. The reason I did that was because I always wanted to learn AutoCAD. Of course I have been making effort by going to YouTube, downloading videos, but I was not able to fully understand because some of the videos and short and not well organised... I just want to encourage each and everyone of you here, especially those that have not gone through this course, I want to tell you that we are in the age of reinventing ourselves. And if you don't reinvent yourselves, you will be left behind because we are talking about technology at this time.",
      story: "Agnes Wurrie discovered Leotech through a friend's social media post and immediately recognized an opportunity to fulfill her long-standing desire to learn AutoCAD. Prior to joining Leotech, Agnes had attempted to teach herself the software through YouTube tutorials but found the resources fragmented and difficult to follow. As Leotech's first female trainee, Agnes joined the inaugural cohort and quickly distinguished herself through her dedication and aptitude. The structured, comprehensive training at Leotech provided what self-directed learning could not – a solid foundation in AutoCAD that she could immediately apply to her work. After completing the program, Agnes secured a position as a Project Engineer at SOGEFEL (SL) Limited, where her AutoCAD skills have proven invaluable in managing construction projects.",
      impact: "As Leotech's first female graduate, Agnes has become an inspiration to other women in engineering. Her success at SOGEFEL (SL) Limited demonstrates the impact of Leotech's training on career advancement. Agnes regularly returns to Leotech as a guest speaker, encouraging others, particularly women, to embrace technology and 'reinvent themselves' through digital skills training. Her journey exemplifies Leotech's commitment to gender diversity in technical fields."
    },
    {
      id: 4,
      name: "Ing. Deborah Momoh",
      role: "Mining Engineer",
      company: "Kingho Mining Company",
      image: "/images/testimonials/deborah_momoh.jpg",
      category: "private-sector",
      year: "2022",
      program: "Chief Architect",
      quote: "I learned about LEOTECH through a friend as I showed interest in the Chief Architect software. As an engineering student at Fourah Bay College, most of this software course, unfortunately, is not included in our curriculum, but it is a great benefit for the career I'm embarking on. As a Sierra Leonean, I want to acquire more knowledge and give back to the country by impacting and developing what I've learned in the classroom into reality. I believe not everyone has access to software skills, but LEOTECH made it possible, and I'm grateful for that. I thank my tutors for creating a conducive and interactive learning environment throughout my course. LEOTECH is surely the place to be if one seeks to learn software programs proficiently.",
      story: "Deborah Momoh was studying mining engineering at Fourah Bay College when she realized that her university curriculum lacked crucial software training needed in today's industry. After hearing about Leotech from a friend, she enrolled in the Chief Architect software course as part of Cohort 4. Despite being in a field not typically associated with architectural design software, Deborah recognized the value of cross-disciplinary skills. The interactive learning environment at Leotech helped her quickly master the software, and she found ways to apply architectural visualization principles to mining project planning. Upon graduation, Deborah secured a position at Kingho Mining Company, where her unique combination of mining engineering knowledge and design software skills set her apart from other candidates. Her ability to create clear visual representations of mining projects has improved communication between technical teams and management.",
      impact: "Deborah's story highlights the importance of cross-disciplinary skills in engineering. Her unique combination of mining engineering and design software expertise has made her particularly valuable to her employer. She has been able to improve project visualization and communication, leading to more efficient implementation of mining operations. Additionally, Deborah's success demonstrates how digital skills can help bridge the gap between theoretical university education and practical industry needs."
    },
    {
      id: 5,
      name: "Ing. Monica Musa",
      role: "IT Professional & Leotech Tutor",
      company: "Leotech",
      image: "/images/testimonials/monica_musa.jpg",
      category: "education",
      year: "2021",
      program: "Computer-Aided Design",
      quote: "Leotech's practical focus and industry connections are unbeatable. It shaped my IT career in the best way possible. Skills taught at Leotech create self-empowerment and this helped me earn my own money; which I am very sure each and every youth that goes through training will reap the same benefit. As an engineering student, I would like to draw your attention on how these skills are important in my career... I can now produce a plan from AutoCAD, Revit and Chief Architect, all thanks to Leotech.",
      story: "Monica Musa joined Leotech's Computer-Aided Design program while still a student. Her aptitude and enthusiasm for the material quickly stood out. Unlike traditional educational settings, Leotech's practical approach resonated with Monica's learning style, allowing her to rapidly develop proficiency in multiple design software platforms including AutoCAD, Revit, and Chief Architect. The skills she acquired enabled her to start earning income as a freelance designer even before completing her studies. Her success and understanding of Leotech's teaching methodology led to an invitation to join the organization as a tutor after her graduation. Today, Monica helps other young people develop the same skills that transformed her own career path, embodying Leotech's peer-to-peer teaching approach.",
      impact: "Monica's journey from trainee to tutor represents a perfect example of Leotech's impact cycle. Not only did she gain valuable skills that provided immediate financial benefits through freelance work, but she now plays a crucial role in transferring those skills to new generations of students. As a female IT professional and tutor, she serves as a role model for other women entering technical fields. Her story demonstrates how Leotech not only builds capacity in individuals but also creates a sustainable cycle of knowledge transfer."
    },
    {
      id: 6,
      name: "Mohamed Sylvanus Kamara",
      role: "Civil Engineer & Training Manager",
      company: "Construction Solutions Ltd.",
      image: "/images/testimonials/mohamed_kamara.jpg",
      category: "private-sector",
      year: "2021",
      program: "Building Information Modeling",
      quote: "The BIM training I received at Leotech completely changed my approach to construction projects. Before joining the program, I was creating 2D drawings that often led to misinterpretations on site. Learning Revit and the BIM methodology opened my eyes to a more collaborative and efficient way of working. The 3D modeling skills helped me secure my current position, where I'm now implementing BIM processes for all our major projects. I also volunteer at Leotech to help other engineers develop these essential skills.",
      story: "Mohamed Sylvanus Kamara was working as a junior civil engineer when he realized that his limited 2D drawing skills were holding back his career advancement. Traditional construction methods in Sierra Leone predominantly relied on 2D drawings, but Mohamed recognized that the industry was evolving globally toward Building Information Modeling (BIM). He enrolled in Leotech's BIM program, where he mastered Revit and other BIM tools. The comprehensive training covered not just software operations but the entire BIM methodology and workflow. Armed with these advanced skills, Mohamed secured a position at Construction Solutions Ltd., where he quickly rose to become Training Manager. In this role, he oversees the implementation of BIM processes across all major projects and trains other staff members. His expertise has helped the company win contracts with international clients who require BIM capabilities.",
      impact: "Mohamed's BIM expertise has positioned him as a leader in Sierra Leone's evolving construction industry. At Construction Solutions Ltd., he has implemented BIM processes that have reduced project errors by 35% and improved completion times by 20%. His role as a volunteer tutor at Leotech allows him to multiply his impact by training the next generation of BIM-proficient engineers. Mohamed's story illustrates how specialized digital skills can accelerate career growth while also advancing industry standards in Sierra Leone."
    },
    {
      id: 7,
      name: "Fatmata Koroma",
      role: "GIS Specialist",
      company: "Environmental Protection Agency",
      image: "/images/testimonials/fatmata_koroma.jpg",
      category: "government",
      year: "2023",
      program: "Land Survey & Photogrammetry",
      quote: "The Land Survey and Photogrammetry program at Leotech gave me practical skills that weren't covered in my university education. Learning to use Global Mapper and drone technology for environmental mapping has been transformative for my work at the EPA. We can now conduct rapid assessments of environmental impact with greater accuracy than ever before. Leotech's hands-on approach to teaching made complex concepts accessible and immediately applicable to real-world challenges.",
      story: "Fatmata Koroma had a background in environmental science but lacked the technical mapping skills needed to advance in her field. After joining Leotech's Land Survey and Photogrammetry program, she gained proficiency in Global Mapper, GIS applications, and drone technology for environmental mapping. These skills were directly applicable to her work at the Environmental Protection Agency, where she now leads mapping initiatives for environmental impact assessments. The program's emphasis on practical, hands-on learning allowed Fatmata to quickly implement her new knowledge in professional settings. Her expertise in using drone technology for rapid environmental assessment has made her department more efficient and effective in monitoring environmental changes and violations.",
      impact: "Fatmata's new skills have revolutionized her department's approach to environmental monitoring. Using drone technology and GIS mapping, she has helped the EPA conduct more comprehensive and timely assessments of environmental conditions across Sierra Leone. Her work has been particularly valuable in tracking illegal mining activities and deforestation, contributing to better environmental protection. Fatmata's story demonstrates how specialized digital skills can enhance government agencies' effectiveness in fulfilling their mandates."
    },
    {
      id: 8,
      name: "Ibrahim Sesay",
      role: "Software Developer",
      company: "TechHub SL",
      image: "/images/testimonials/ibrahim_sesay.jpg",
      category: "tech",
      year: "2023",
      program: "Computer Programming",
      quote: "I always had an interest in technology but had no formal pathway to develop my programming skills. Leotech's Computer Programming course provided the structure and guidance I needed. Learning Python and JavaScript opened up a world of possibilities. The project-based approach meant I finished the program with actual portfolio pieces that I could show to potential employers. Within two months of completing the course, I secured a job at TechHub SL, where I'm now developing applications that serve local businesses.",
      story: "Ibrahim Sesay had a natural aptitude for technology but lacked formal education in computer science. Working as a mobile phone repair technician, he dreamed of becoming a software developer but couldn't afford university tuition. Leotech's Computer Programming course offered an affordable entry point into the world of professional programming. Ibrahim thrived in the hands-on learning environment, mastering Python and JavaScript while building practical projects. The mentorship provided by Leotech's tutors helped him navigate the challenges of learning to code and guided his independent learning beyond the curriculum. By the end of the program, Ibrahim had developed a portfolio of projects that demonstrated his capabilities to potential employers. TechHub SL recognized his talent and hired him as a junior developer, where he now creates applications for local businesses and contributes to Sierra Leone's growing tech ecosystem.",
      impact: "Ibrahim's transition from phone repair technician to software developer represents a significant career advancement and income increase. At TechHub SL, he develops applications that help local businesses digitize their operations, creating a multiplier effect that benefits the broader economy. His success story has inspired other young people from non-traditional educational backgrounds to pursue programming careers. Ibrahim regularly mentors newer Leotech students, sharing his knowledge and experience to help them achieve similar transformations."
    }
  ];

  // Success story categories
  const categories = [
    { id: 'all', name: 'All Stories' },
    { id: 'government', name: 'Government Sector' },
    { id: 'private-sector', name: 'Private Sector' },
    { id: 'freelance', name: 'Freelance' },
    { id: 'tech', name: 'Technology' },
    { id: 'education', name: 'Education' }
  ];
  
  // Programs offered by Leotech
  const programs = [
    { id: 'all', name: 'All Programs' },
    { id: 'AutoCAD & Global Mapper', name: 'AutoCAD & Global Mapper' },
    { id: 'Chief Architect', name: 'Chief Architect' },
    { id: 'AutoCAD', name: 'AutoCAD' },
    { id: 'Building Information Modeling', name: 'Building Information Modeling' },
    { id: 'Land Survey & Photogrammetry', name: 'Land Survey & Photogrammetry' },
    { id: 'Computer Programming', name: 'Computer Programming' },
    { id: 'Computer-Aided Design', name: 'Computer-Aided Design' }
  ];
  
  // Filter success stories based on category and search term
  useEffect(() => {
    const filtered = successStoriesData.filter(story => {
      const matchesCategory = activeFilter === 'all' || story.category === activeFilter;
      const matchesSearch = 
        story.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        story.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        story.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        story.program.toLowerCase().includes(searchTerm.toLowerCase()) ||
        story.quote.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesCategory && matchesSearch;
    });
    
    setFilteredStories(filtered);
  }, [activeFilter, searchTerm]);
  
  // Effect to scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  
  // Calculate impact metrics
  const metrics = {
    totalGraduates: 700,
    employmentRate: 85,
    averageIncrease: 60,
    entrepreneurshipRate: 25
  };
  
  return (
    <>
      <Helmet>
        <title>Success Stories - Leotech SL</title>
        <meta name="description" content="Discover inspiring success stories of Leotech graduates who have transformed their careers through our digital skills training programs." />
      </Helmet>
      
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-16 overflow-hidden"
        style={{
          backgroundImage: 'url(/images/success-stories/success-hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-75"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">Success Stories</span>
          </div>
          
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              Success Stories
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-xl text-white text-opacity-90 mb-8"
            >
              Meet our graduates who have transformed their careers and lives through Leotech's digital skills training programs.
            </motion.p>
            
            {/* Search Bar */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative max-w-xl mx-auto"
            >
              <input
                type="text"
                placeholder="Search success stories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 px-6 pr-12 bg-white bg-opacity-95 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-secondary"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                {searchTerm ? (
                  <button onClick={() => setSearchTerm('')} className="hover:text-primary">
                    <FaTimes />
                  </button>
                ) : (
                  <FaSearch />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Impact Metrics Section */}
      <Section background="white" padding="lg">
        <div className="text-center mb-12">
          <Heading level={2} align="center" className="mb-4" withAccent>
            Our Impact in Numbers
          </Heading>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Measurable results from our commitment to empowering youth with marketable digital skills
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-primary text-center"
          >
            <div className="h-16 w-16 bg-primary-light bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaGraduationCap className="text-3xl text-primary" />
            </div>
            <div className="text-4xl font-bold text-primary-dark mb-2">{metrics.totalGraduates}+</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Successful Trainees</h3>
            <p className="text-gray-600">Youth trained in digital skills since our founding</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-secondary text-center"
          >
            <div className="h-16 w-16 bg-secondary bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaBuilding className="text-3xl text-secondary" />
            </div>
            <div className="text-4xl font-bold text-primary-dark mb-2">{metrics.employmentRate}%</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Employment Rate</h3>
            <p className="text-gray-600">Graduates who find relevant employment within 6 months</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-green-500 text-center"
          >
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaChartLine className="text-3xl text-green-600" />
            </div>
            <div className="text-4xl font-bold text-primary-dark mb-2">{metrics.averageIncrease}%</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Income Increase</h3>
            <p className="text-gray-600">Average income improvement after completion</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-purple-500 text-center"
          >
            <div className="h-16 w-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaUser className="text-3xl text-purple-600" />
            </div>
            <div className="text-4xl font-bold text-primary-dark mb-2">{metrics.entrepreneurshipRate}%</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Entrepreneurs</h3>
            <p className="text-gray-600">Graduates who start their own businesses</p>
          </motion.div>
        </div>
      </Section>
      
      {/* Categories Filter */}
      <div className="bg-white border-t border-b border-gray-200 sticky top-16 z-20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap justify-between items-center gap-4">
            {/* Category Filter */}
            <div className="flex items-center">
              <div className="flex items-center text-gray-700 mr-3">
                <FaFilter className="mr-2" /> Filter by:
              </div>
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-700 py-2 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Clear Filters */}
            {(activeFilter !== 'all' || searchTerm) && (
              <button
                onClick={() => {
                  setActiveFilter('all');
                  setSearchTerm('');
                }}
                className="text-primary font-medium hover:text-primary-dark flex items-center"
              >
                <FaTimes className="mr-2" /> Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Success Stories Grid */}
      <Section background="gray" padding="lg">
        <div className="mb-8">
          <Heading level={2}>
            Graduate Success Stories
          </Heading>
          <p className="text-gray-600">
            {filteredStories.length} stor{filteredStories.length !== 1 ? 'ies' : 'y'} found
            {searchTerm && ` matching "${searchTerm}"`}
            {activeFilter !== 'all' && ` in ${categories.find(c => c.id === activeFilter)?.name}`}
          </p>
        </div>
        
        {filteredStories.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <div className="text-gray-400 text-5xl mb-4">
              <FaUser className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Success Stories Found</h3>
            <p className="text-gray-600 mb-6">
              We couldn't find any success stories matching your criteria.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setActiveFilter('all');
                setSearchTerm('');
              }}
            >
              View All Success Stories
            </Button>
          </div>
        ) : (
          <motion.div
            className="space-y-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredStories.map((story) => (
              <motion.div
                key={story.id}
                variants={itemVariants}
                className={`bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 transform transition-all duration-300 ${
                  expandedStory === story.id ? 'ring-2 ring-primary' : 'hover:-translate-y-1 hover:shadow-lg'
                }`}
              >
                <div className="grid grid-cols-1 md:grid-cols-4">
                  {/* Profile Image */}
                  <div className="md:col-span-1 flex items-center justify-center p-6 bg-primary-dark">
                    <div className="relative">
                      <div className="w-45 h-45 rounded-full overflow-hidden border-4 border-white">
                        <img
                          src={story.image}
                          alt={story.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://placehold.co/300x300/1a237e/ffffff?text=${story.name.split(' ').map(n => n[0]).join('')}`;
                          }}
                        />
                      </div>
                      <div className="absolute -bottom-2 -right-2 bg-secondary text-white text-sm px-2 py-1 rounded-full">
                        {story.year}
                      </div>
                    </div>
                  </div>
                  
                  {/* Story Details */}
                  <div className="md:col-span-3 p-6">
                    <div className="flex flex-col h-full">
                      <div className="mb-auto">
                        <div className="flex flex-wrap justify-between items-start mb-3">
                          <div>
                            <h3 className="text-2xl font-bold text-primary-dark">{story.name}</h3>
                            <p className="text-gray-600">
                              {story.role} at {story.company}
                            </p>
                          </div>
                          <span className="inline-block bg-blue-100 text-primary-dark text-xs font-medium rounded-full px-3 py-1">
                            {story.program}
                          </span>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg mb-4 relative">
                          <FaQuoteLeft className="absolute text-gray-200 text-4xl top-2 left-2" />
                          <p className="text-gray-700 italic pl-8 relative z-10">
                            {expandedStory === story.id 
                              ? story.quote 
                              : `${story.quote.substring(0, 220)}${story.quote.length > 220 ? '...' : ''}`}
                          </p>
                          
                          {story.quote.length > 220 && (
                            <button
                              onClick={() => setExpandedStory(expandedStory === story.id ? null : story.id)}
                              className="text-primary font-medium hover:text-primary-dark mt-2"
                            >
                              {expandedStory === story.id ? 'Show Less' : 'Read Full Quote'}
                            </button>
                          )}
                        </div>
                        
                        {expandedStory === story.id && (
                          <>
                            <div className="mb-4">
                              <h4 className="font-bold text-primary-dark mb-2">Success Story</h4>
                              <p className="text-gray-700">{story.story}</p>
                            </div>
                            
                            <div className="mb-4">
                              <h4 className="font-bold text-primary-dark mb-2">Impact</h4>
                              <p className="text-gray-700">{story.impact}</p>
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="mt-4">
                        {expandedStory !== story.id && (
                          <Button 
                            variant="primary" 
                            onClick={() => setExpandedStory(story.id)}
                          >
                            Read Full Story
                          </Button>
                        )}
                        
                        {expandedStory === story.id && (
                          <Button 
                            variant="outline-primary"
                            onClick={() => setExpandedStory(null)}
                          >
                            Collapse Story
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </Section>
      
      {/* Featured Testimonials Section */}
      <Section background="white" padding="lg">
        <div className="text-center mb-12">
          <Heading level={2} align="center" className="mb-4" withAccent>
            What Our Graduates Say
          </Heading>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Hear directly from our graduates about how Leotech has impacted their careers and lives
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {successStoriesData.slice(0, 3).map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100"
            >
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-14 h-14 rounded-full overflow-hidden mr-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://placehold.co/100x100/1a237e/ffffff?text=${testimonial.name.split(' ').map(n => n[0]).join('')}`;
                      }}
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-primary-dark">{testimonial.name}</h3>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
                
                <div className="flex text-secondary mb-3">
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                </div>
                
                <div className="mb-4">
                  <FaQuoteLeft className="text-gray-200 text-2xl mb-2" />
                  <p className="text-gray-700 italic">
                    "{testimonial.quote.substring(0, 150)}..."
                  </p>
                </div>
                
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => {
                    setExpandedStory(testimonial.id);
                    window.scrollTo({
                      top: document.getElementById('success-stories').offsetTop - 100,
                      behavior: 'smooth'
                    });
                  }}
                >
                  Read Full Story
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>
      
      {/* Programs Overview Section */}
      <Section background="gray" padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Heading level={2} className="mb-6" withAccent>
              Our Training Programs
            </Heading>
            <p className="text-gray-700 mb-6">
              The success stories you read here are the result of our carefully designed training programs. 
              Each program focuses on practical, in-demand skills that prepare our trainees for the modern workforce.
            </p>
            
            <div className="space-y-4 mb-8">
              {[
                {
                  title: "Building Information Modeling",
                  description: "Master Revit, Chief Architect, and other BIM tools"
                },
                {
                  title: "Computer-Aided Design",
                  description: "Learn AutoCAD, Fusion360, and Autodesk Inventor"
                },
                {
                  title: "Land Survey & Photogrammetry",
                  description: "Global Mapper, GIS, and drone technology for surveying"
                },
                {
                  title: "Computer Programming",
                  description: "Develop skills in Python, JavaScript, and web development"
                }
              ].map((program, index) => (
                <div key={index} className="flex items-start">
                  <div className="h-6 w-6 rounded-full bg-primary-light bg-opacity-20 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                    <FaCheck className="text-primary text-xs" />
                  </div>
                  <div>
                    <h4 className="font-bold text-primary-dark">{program.title}</h4>
                    <p className="text-gray-600">{program.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <Button 
              variant="primary" 
              to="/programs" 
              size="lg"
            >
              Explore All Programs
            </Button>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-white p-6 rounded-lg shadow-xl"
          >
            <h3 className="text-xl font-bold text-primary-dark mb-6">Why Choose Leotech?</h3>
            <div className="space-y-6">
              {[
                {
                  title: "Practical, Hands-on Training",
                  description: "Our emphasis on practical skills ensures you can apply what you learn immediately in the workplace.",
                  icon: <FaGraduationCap className="text-primary" />
                },
                {
                  title: "Taught by Industry Professionals",
                  description: "Learn from practitioners who bring real-world experience to the classroom.",
                  icon: <FaUser className="text-primary" />
                },
                {
                  title: "Short, Focused Programs",
                  description: "3-month courses designed to quickly build your skills without lengthy time commitments.",
                  icon: <FaClock className="text-primary" />
                },
                {
                  title: "Strong Industry Connections",
                  description: "Our network of partners helps connect graduates with employment opportunities.",
                  icon: <FaBuilding className="text-primary" />
                }
              ].map((item, index) => (
                <div key={index} className="flex">
                  <div className="h-10 w-10 rounded-full bg-primary-light bg-opacity-20 flex items-center justify-center mr-4 flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="font-bold text-primary-dark mb-1">{item.title}</h4>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </Section>
      
      {/* Call to Action */}
      <Section background="gradient-primary" padding="lg">
        <div className="text-center max-w-3xl mx-auto">
          <Heading level={2} color="white" align="center" className="mb-6">
            Start Your Success Story
          </Heading>
          <p className="text-xl text-white text-opacity-90 mb-10">
            Join the ranks of our successful graduates. Enroll in one of our programs today
            and take the first step toward transforming your career.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Button 
              variant="secondary" 
              size="lg" 
              to="/apply"
            >
              Apply Now
            </Button>
            <Button 
              variant="light" 
              size="lg" 
              to="/contact"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};

export default SuccessStoriesPage;